/**
 * JMC operations dashboard — the platform-wide view.
 *
 * A JMC operator monitors everything: every clinic, every store, every user,
 * and the state of each. All of that is *metadata*, which is why it needs no
 * elevation. Article content is the one thing missing from this screen, and it
 * stays missing: reading a clinic's text requires a time-limited, reason-bound,
 * audited support elevation.
 *
 * Which four figures lead is a choice about who reads this screen. A user count
 * is an inventory number: it changes when somebody is hired, it lives on the
 * users screen, and nobody opens operations to look at it. Vendor AI spend is the
 * one figure on the platform that moves without anybody registering anything, is
 * denominated in a currency JMC actually pays, and is deliberately invisible to
 * clinics - see ClinicDashboard.tsx. So it takes the tile, and the per-clinic
 * table below answers the question the tile always provokes: because of whom.
 */
import { Link, useLoaderData } from 'react-router';
import type { AdminOverview } from '../api.ts';
import {
  Card,
  Empty,
  Meter,
  Pill,
  RowLink,
  StatusPill,
  Table,
  jaDate,
  jaNumber,
} from '../components/ui.tsx';
import { SkeletonRows, SkeletonTiles, usePendingScreen } from '../components/Skeleton.tsx';
import { AnalyticsIcon, ArticleIcon, ClinicIcon, StoreIcon } from '../components/icons.tsx';
import { groupActivity, type ActivityEntry } from '../admin-activity.ts';
import { usePaged } from '../paging.ts';

function StatTile({
  icon,
  label,
  value,
  hint,
}: {
  icon: React.ReactNode;
  label: string;
  value: number | string;
  hint?: React.ReactNode;
}) {
  return (
    <div className="stat">
      <div className="stat__head">
        <span className="stat__icon">{icon}</span>
        <span className="stat__label">{label}</span>
      </div>
      <div className="stat__value">{value}</div>
      {hint && <div className="muted small">{hint}</div>}
    </div>
  );
}

/**
 * Micro-USD as money.
 *
 * Integers all the way from the ledger - see clinic_usage_periods - divided only
 * here, at the point where it becomes text nobody does arithmetic on again. Cents
 * disappear past $10 because at that size they are noise in a tile.
 */
const usd = (micro: number | string): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: Number(micro) < 10_000_000 ? 2 : 0,
  }).format(Number(micro) / 1_000_000);

/** Audit actions rendered in the operator's language, not as raw keys. */
const ACTION_LABEL: Record<string, string> = {
  'clinic.created': 'クリニックを登録しました',
  'clinic.updated': 'クリニック情報を更新しました',
  'clinic.status_changed': 'クリニックの状態を変更しました',
  'clinic.settings_updated': 'クリニック設定を更新しました',
  'store.created': '店舗を登録しました',
  'store.updated': '店舗情報を更新しました',
  'store.settings_updated': '店舗設定を更新しました',
  'membership.granted': 'ユーザーを追加しました',
  'membership.updated': 'ユーザーの権限を変更しました',
  'user.updated': 'ユーザー情報を更新しました',
  'user.profile_updated': 'プロフィールを更新しました',
  'user.platform_role_granted': 'JMC管理者権限を付与しました',
  'user.platform_role_revoked': 'JMC管理者権限を解除しました',
  'membership.revoked': '所属を解除しました',
  'support.elevation_granted': 'サポート権限を昇格しました',
  'support.elevation_revoked': 'サポート権限を解除しました',
  'prompt.created': 'プロンプトを作成しました',
  'prompt.version_created': 'プロンプト版を作成しました',
  'prompt.version_activated': 'プロンプト版を有効化しました',
  'ai_model.created': 'AIモデルを追加しました',
  'ai_model.updated': 'AIモデルを更新しました',
  'ai_model.activated': 'AIモデルを切り替えました',
  'ai_model.deleted': 'AIモデルを削除しました',
  'article.created': '記事を作成しました',
  'article.generated': '記事を生成しました',
  'article.updated': '記事を更新しました',
  'article.exported': '記事を書き出しました',
  'article.archived': '記事を取り下げました',
  'fact.ai_drafted': '情報をAIで下書きしました',
  'authz.scope_denied': '権限外アクセスを拒否しました',
  'authz.no_scope': 'クリニック未選択のためアクセスを拒否しました',
  'authz.platform_denied': '管理者権限のないアクセスを拒否しました',
  'auth.mfa_required': '多要素認証が未達のためアクセスを拒否しました',
  'auth.step_up': '再認証を完了しました',
  'auth.step_up_rejected': '再認証を拒否しました',
  'auth.login': 'ログインしました',
  'auth.dev_login': '開発ログインを行いました',
  'auth.login_rejected': 'ログインを拒否しました',
  'auth.logout': 'ログアウトしました',
  'auth.scope_switched': '作業クリニックを切り替えました',
  'auth.password_changed': 'パスワードを変更しました',
  'auth.password_change_rejected': 'パスワード変更を拒否しました',
  'platform.settings_updated': 'JMCの設定を更新しました',
  'demo.created': 'デモデータを作成しました',
  'demo.removed': 'デモデータを削除しました',
};

const actionLabel = (entry: ActivityEntry): string => ACTION_LABEL[entry.action] ?? entry.action;

/**
 * A clinic group in one line.
 *
 * Two distinct actions named, then what is left over as a count. Naming all of
 * them turns a feed row into a paragraph; naming none of them leaves a number
 * nobody can judge.
 */
const summarise = (entries: ActivityEntry[]): string => {
  const labels = [...new Set(entries.map(actionLabel))];
  const shown = labels.slice(0, 2);
  const rest = entries.filter((entry) => !shown.includes(actionLabel(entry))).length;
  return rest > 0 ? `${shown.join('・')} ほか${jaNumber(rest)}件` : shown.join('・');
};

/**
 * Where a follow-up is actually dealt with.
 *
 * The panel used to be counts under one "see all" that went to the same screen
 * for every row, which is why it was not clear what it was for. Each row now goes
 * to the screen where that particular thing is fixed.
 */
const TASK_LINK: Record<string, string> = {
  clinic_without_plan: '/admin/tenants?tab=clinics',
  clinic_without_store: '/admin/tenants?tab=clinics',
  clinic_suspended: '/admin/tenants?tab=clinics',
  user_without_membership: '/admin/users',
  prompt_without_active_version: '/admin/prompts',
  support_elevation_active: '/admin/audit?action=support',
};

/** Why the row is on the list at all — a count on its own never says. */
const TASK_NOTE: Record<string, string> = {
  clinic_without_plan: '契約プランがないため、記事の作成枠を持てません',
  clinic_without_store: '店舗がないため、記事を作成できません',
  clinic_suspended: '利用停止中で、ログインしても操作できません',
  user_without_membership: 'どのクリニックにも所属がなく、何も操作できません',
  prompt_without_active_version: '有効な版がないため、既定のプロンプトで生成されます',
  support_elevation_active: '記事本文を閲覧できる状態です。期限まで有効です',
};

const relativeTime = (iso: string): string => {
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (minutes < 1) return 'たった今';
  if (minutes < 60) return `${minutes}分前`;
  if (minutes < 60 * 24) return `${Math.round(minutes / 60)}時間前`;
  return `${Math.round(minutes / (60 * 24))}日前`;
};

export default function AdminDashboard() {
  const { counts, aiUsage, corpus, expiring, recentClinics, recentStores, tasks, activity } =
    useLoaderData<AdminOverview>();
  const pending = usePendingScreen();

  const spend = Number(counts.ai_micro_cost_used);
  /*
   * The budget JMC set for itself, or nothing. It used to be the sum of every
   * clinic's plan allowance, which produced "0% of $65" - a percentage of a
   * number nobody had chosen. Unset shows the spend alone.
   */
  const budget = counts.ai_budget_micro === null ? null : Number(counts.ai_budget_micro);
  const spendRatio = budget === null ? 0 : spend / budget;
  const groups = groupActivity(activity);

  /*
   * What is wrong with the corpus, in the order it makes a review less true.
   * Nothing wrong means no panel: a card that is present every day saying
   * "fine" is a card nobody reads on the day it stops saying that.
   */
  const corpusIssues: { text: string; severe: boolean }[] = [];
  if (corpus.active_version === null) {
    corpusIssues.push({
      text:
        Number(corpus.sources) === 0
          ? '資料が1件も登録されていません。法令チェックは表現ルールのみで動作しています。'
          : '有効なリリースがありません。資料は登録されていますが、法令チェックは条文を参照していません。',
      severe: true,
    });
  } else {
    if (Number(corpus.unembedded) > 0) {
      corpusIssues.push({
        text: `有効なリリース v${corpus.active_version} のうち ${jaNumber(corpus.unembedded)} チャンクが未ベクトル化です。言い換えの検索が効かず、語句が一致する条文しか見つかりません。`,
        severe: false,
      });
    }
    if (!corpus.active_digest) {
      corpusIssues.push({
        text: `有効なリリース v${corpus.active_version} に遵守事項が設定されていません。記事の生成時に法令上の注意が渡されていません。`,
        severe: false,
      });
    }
  }
  /*
   * The version that landed and never went live. This is what a working
   * re-fetch schedule produces if nobody acts on it: the corpus is current, the
   * corpus IN USE is a quarter old, and every screen says the checks are
   * running.
   */
  if (corpus.active_version !== null && Number(corpus.unreleased) > 0) {
    corpusIssues.push({
      text: `${jaNumber(corpus.unreleased)}件の新しい版が、有効なリリース v${corpus.active_version} に含まれていません。${
        Number(corpus.drafts) > 0
          ? '作成済みの下書きリリースを確認して有効化してください。'
          : 'リリースを作成して有効化してください。'
      }`,
      severe: true,
    });
  }
  if (Number(corpus.stale_sources) > 0) {
    corpusIssues.push({
      text: `${jaNumber(corpus.stale_sources)}件の資料が${jaNumber(corpus.refetchDays)}日以上確認されていません。発行元で改定がないか確認し、必要なら再取得してください。`,
      severe: false,
    });
  }
  if (Number(corpus.never_fetched) > 0) {
    corpusIssues.push({
      text: `${jaNumber(corpus.never_fetched)}件の資料は本文が未取込のため、チェックには使われていません。`,
      severe: false,
    });
  }

  /*
   * The top five by spend, which is what the card is for; the rest on request.
   * autoLoad off, because five rows do not fill a screen - an observer would
   * see its own sentinel the moment the page rendered and quietly undo the
   * "top five" this card exists to be. After the first press it loads on scroll
   * like any other list.
   */
  const clinicUsage = usePaged<AdminOverview['aiUsage'][number]>(
    '/admin/ai-usage',
    aiUsage,
    5,
    false,
  );

  return (
    <>
      {/*
        No banner. The sidebar already says which workspace this is, and a
        gradient strip repeating it pushed the figures - the reason anyone opens
        this screen - below the fold. The action it carried moved into the card
        that the figures live in.
      */}
      <Card
        title="登録状況"
        actions={
          <Link className="btn btn--sm" to="/admin/clinics/new">
            ＋ クリニックを登録
          </Link>
        }
      >
        {pending ? (
          <SkeletonTiles />
        ) : (
          <div className="grid grid--4">
            <StatTile
              icon={<ClinicIcon />}
              label="登録クリニック数"
              value={jaNumber(counts.clinics)}
              hint={`稼働中 ${counts.clinics_active}`}
            />
            <StatTile
              icon={<StoreIcon />}
              label="登録店舗数"
              value={jaNumber(counts.stores)}
              hint={`稼働中 ${counts.stores_active}`}
            />
            {/*
              The AI bill. Shown against what the sold plans allow, because a
              spend figure with no ceiling beside it cannot be judged.
            */}
            <StatTile
              icon={<AnalyticsIcon />}
              label="今期のAI利用額"
              value={usd(spend)}
              hint={
                budget === null ? (
                  <Link to="/admin/settings">月間予算を設定する</Link>
                ) : (
                  <>
                    月間予算 {usd(budget)} の {Math.round(spendRatio * 100)}%
                    {spendRatio >= 0.8 && (
                      <>
                        {' '}
                        <Pill tone={spendRatio >= 1 ? 'danger' : 'warn'}>要確認</Pill>
                      </>
                    )}
                  </>
                )
              }
            />
            <StatTile
              icon={<ArticleIcon />}
              label="今期の記事作成数"
              value={jaNumber(counts.articles_this_month)}
              hint={`画像 ${jaNumber(counts.images_this_month)} 枚・全クリニック合計`}
            />
          </div>
        )}
      </Card>

      {/*
        Whether the legal corpus is switched on.

        The panel appears only when something is wrong with it, and it is worth
        a whole panel because the failure is invisible everywhere else: with no
        active release, every article check still runs, still returns a verdict,
        and still says 指摘なし - having consulted no law at all. A clinic
        reading that sees a clean review.
      */}
      {!pending && corpusIssues.length > 0 && (
        <Card
          title="法令コーパスの状態"
          caption="記事の法令チェックが実際に条文を参照できているかどうかです。"
          actions={
            <Link className="btn btn--sm btn--ghost" to="/admin/legal">
              法令/RAG資料管理へ
            </Link>
          }
        >
          <ul className="feed">
            {corpusIssues.map((issue) => (
              <li key={issue.text}>
                <span
                  className="feed__dot"
                  style={
                    issue.severe
                      ? { background: 'var(--danger)' }
                      : { background: 'var(--warn)' }
                  }
                />
                <span>{issue.text}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/*
        Contracts that have run out, or are about to.

        Above the spend, because it is the only thing on this screen with a
        deadline attached: an ended contract means that clinic's staff are being
        refused every generation right now, and a renewal is one press away on
        the clinic's own page. Absent entirely when there is nothing to do -
        an empty panel every day is how a panel stops being read.
      */}
      {!pending && expiring.length > 0 && (
        <Card
          title="契約期間の終了が近いクリニック"
          caption="終了すると、そのクリニックのAI機能（記事・画像の生成、サイトからの読み取り）が停止します。ログインや既存記事の閲覧・編集は続けて行えます。"
        >
          <Table
            head={
              <>
                <th scope="col">クリニック</th>
                <th scope="col">プラン</th>
                <th scope="col">契約終了日</th>
                <th scope="col">状態</th>
              </>
            }
          >
            {expiring.map((row) => (
              <tr key={row.id} className="row-link">
                <td>
                  <RowLink to={`/admin/clinics/${row.id}`}>{row.name}</RowLink>
                </td>
                <td>{row.plan_id ? <Pill>{row.plan_id}</Pill> : <Pill tone="warn">未設定</Pill>}</td>
                <td>{jaDate(row.ends_at)}</td>
                <td>
                  {row.ended ? (
                    <Pill tone="danger">終了（AI機能停止中）</Pill>
                  ) : (
                    <Pill tone="warn">
                      残り{jaNumber(Math.max(0, row.days_left))}日
                    </Pill>
                  )}
                </td>
              </tr>
            ))}
          </Table>
        </Card>
      )}

      {/*
        Whose spend it is. The tile says how much; the operator's next question is
        always which clinic, and that answer is the one with an action attached.
      */}
      <Card
        title="AI利用状況（クリニック別）"
        caption="各クリニックの現在の請求期間の実績です。上限は契約プランの月額AI利用枠で、超えると生成が止まります。"
        actions={
          <Link className="btn btn--ghost btn--sm" to="/admin/analytics">
            推移を見る
          </Link>
        }
      >
        {pending ? (
          <SkeletonRows rows={5} columns={6} />
        ) : aiUsage.length === 0 ? (
          <Empty>まだ利用実績がありません。</Empty>
        ) : (
          <Table
            head={
              <>
                <th scope="col">クリニック</th>
                <th scope="col" className="num">
                  AI利用額
                </th>
                <th scope="col">利用枠</th>
                <th scope="col" className="num">
                  記事
                </th>
                <th scope="col" className="num">
                  画像
                </th>
                <th scope="col">期間終了</th>
              </>
            }
          >
            {clinicUsage.items.map((row) => {
              const used = Number(row.ai_micro_cost_used);
              const limit =
                row.ai_micro_cost_limit === null ? null : Number(row.ai_micro_cost_limit);
              return (
                <tr key={row.id} className="row-link">
                  <td>
                    <RowLink to={`/admin/clinics/${row.id}`}>{row.name}</RowLink>
                    {row.status !== 'ACTIVE' && (
                      <>
                        {' '}
                        <StatusPill status={row.status} />
                      </>
                    )}
                  </td>
                  <td className="num">{usd(used)}</td>
                  <td>
                    {limit === null ? (
                      <span className="muted">プラン未設定</span>
                    ) : (
                      <Meter used={used} limit={limit} label={`${row.name}のAI利用枠`} />
                    )}
                  </td>
                  <td className="num">{jaNumber(row.articles_used)}</td>
                  <td className="num">{jaNumber(row.images_used)}</td>
                  <td>{jaDate(row.period_end)}</td>
                </tr>
              );
            })}
          </Table>
        )}

        {/*
          The end of the list, and the request for what comes after it. The
          button is the way in; once it has been pressed the observer takes
          over, so a long list keeps loading as it is scrolled.
        */}
        {!pending && aiUsage.length > 0 && !clinicUsage.done && (
          <div ref={clinicUsage.sentinel} className="more-rows">
            <button
              className="btn btn--ghost btn--sm"
              type="button"
              onClick={clinicUsage.more}
              disabled={clinicUsage.loading}
            >
              {clinicUsage.loading
                ? '読み込み中…'
                : /*
                     How many are still hidden, from the clinic count already on
                     this screen - a card showing exactly five rows gives no
                     sign that there is a sixth.
                   */
                  `さらに表示（残り${jaNumber(Math.max(0, counts.clinics - clinicUsage.items.length))}件）`}
            </button>
          </div>
        )}
        {clinicUsage.error && (
          <p className="alert" role="alert">
            続きを読み込めませんでした。画面を再読み込みしてください。
          </p>
        )}
      </Card>

      <div className="grid--rail">
        <div className="grid grid--2">
          <Card
            title="最近登録されたクリニック"
            actions={
              <Link className="btn btn--ghost btn--sm" to="/admin/tenants?tab=clinics">
                すべて見る
              </Link>
            }
          >
            {pending ? (
              <SkeletonRows rows={5} columns={3} />
            ) : recentClinics.length === 0 ? (
              <Empty>クリニックが登録されていません。</Empty>
            ) : (
              <Table
                head={
                  <>
                    <th scope="col">クリニック名</th>
                    <th scope="col" className="num">
                      店舗
                    </th>
                    <th scope="col">登録日</th>
                  </>
                }
              >
                {recentClinics.map((clinic) => (
                  <tr key={clinic.id} className="row-link">
                    <td>
                      <RowLink to={`/admin/clinics/${clinic.id}`}>{clinic.name}</RowLink>
                      {clinic.status !== 'ACTIVE' && (
                        <>
                          {' '}
                          <StatusPill status={clinic.status} />
                        </>
                      )}
                    </td>
                    <td className="num">{jaNumber(clinic.store_count)}</td>
                    <td>{jaDate(clinic.created_at)}</td>
                  </tr>
                ))}
              </Table>
            )}
          </Card>

          <Card
            title="最近登録された店舗"
            actions={
              <Link className="btn btn--ghost btn--sm" to="/admin/tenants?tab=stores">
                すべて見る
              </Link>
            }
          >
            {pending ? (
              <SkeletonRows rows={5} columns={2} />
            ) : recentStores.length === 0 ? (
              <Empty>店舗が登録されていません。</Empty>
            ) : (
              <Table
                head={
                  <>
                    <th scope="col">店舗名（クリニック）</th>
                    <th scope="col">登録日</th>
                  </>
                }
              >
                {recentStores.map((store) => (
                  <tr key={store.id} className="row-link">
                    <td>
                      <RowLink to={`/admin/stores/${store.id}`}>{store.name}</RowLink>
                      <span className="muted small" style={{ display: 'block' }}>
                        {store.clinic_name}
                      </span>
                    </td>
                    <td>{jaDate(store.created_at)}</td>
                  </tr>
                ))}
              </Table>
            )}
          </Card>
        </div>

        <div className="grid">
          {/*
            Was "対応中タスク", which read as a queue somebody was working through.
            Nothing is being worked through: these are conditions that are true
            right now and stop being listed when they stop being true. Each row
            names why it matters and links to where it is fixed.
          */}
          <Card
            title="対応が必要な状態"
            caption="登録内容からその都度算出しています。行を選ぶと、その対応を行う画面を開きます。"
          >
            {tasks.length === 0 ? (
              <Empty>対応が必要な状態はありません。</Empty>
            ) : (
              <ul className="feed">
                {tasks.map((task) => (
                  <li key={task.kind}>
                    <span className="feed__dot" />
                    <span>
                      <Link to={TASK_LINK[task.kind] ?? '/admin/tenants'}>{task.label}</Link>
                      {TASK_NOTE[task.kind] && (
                        <span className="muted small" style={{ display: 'block' }}>
                          {TASK_NOTE[task.kind]}
                        </span>
                      )}
                    </span>
                    <span className="feed__count">{jaNumber(task.count)}</span>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {/*
            Grouped, not chronological. See admin-activity.ts: one clinic's
            housekeeping is one row, while the changes that affect every clinic -
            and anything refused - stay on their own, highlighted.
          */}
          <Card
            title="最近の管理操作"
            caption="色の付いた行は全体への変更・権限昇格・拒否されたアクセスです。それ以外はクリニックごとにまとめています。"
            actions={
              <Link className="btn btn--ghost btn--sm" to="/admin/audit">
                すべて見る
              </Link>
            }
          >
            {groups.length === 0 ? (
              <Empty>記録された操作はまだありません。</Empty>
            ) : (
              <ul className="feed">
                {groups.map((group) => (
                  <li key={group.key} className={group.platform ? 'feed--admin' : undefined}>
                    <span
                      className="feed__dot"
                      style={
                        group.entries[0]!.outcome === 'DENIED'
                          ? { background: 'var(--danger)' }
                          : undefined
                      }
                    />
                    <span>
                      {group.platform ? (
                        <>
                          {actionLabel(group.entries[0]!)}
                          <span className="muted small" style={{ display: 'block' }}>
                            {group.clinicName ?? 'プラットフォーム全体'}
                          </span>
                        </>
                      ) : (
                        <>
                          <Link to={`/admin/audit?clinicId=${group.clinicId}`}>
                            {group.clinicName ?? 'クリニック不明'}
                          </Link>
                          <span className="muted small" style={{ display: 'block' }}>
                            {summarise(group.entries)}
                          </span>
                        </>
                      )}
                    </span>
                    {!group.platform && group.entries.length > 1 && (
                      <span className="feed__count">{jaNumber(group.entries.length)}</span>
                    )}
                    <span className="feed__time">{relativeTime(group.latest)}</span>
                  </li>
                ))}
              </ul>
            )}
            <p className="muted small" style={{ marginBottom: 0 }}>
              権限付与・停止・リリース・拒否されたアクセスの記録です。記事本文やプロンプトは含まれません。
            </p>
          </Card>
        </div>
      </div>
    </>
  );
}
